import type {
  BedrockCatalogModel,
  BedrockModelRole,
  ModelCapabilities,
  BedrockModelCustomerPrice,
  BedrockModelCost,
  BedrockModelRequest,
  VisionSettings,
} from "@/shared/domain/orgs";

import {
  visionDraftError,
  visionDraftFromOverrides,
  visionOverridesFromDraft,
  type VisionDraft,
} from "./visionSettingsForm";

/**
 * RAU per US dollar: 1 RAU (agent unit, one AI credit) is $0.10, so a
 * customer price of cost × this breaks even. Matches the backend's `_RAU_PER_USD`.
 */
export const BREAK_EVEN_RAU_PER_USD = 10;

/** One set of per-million-token rates (AWS cost or customer price), as typed text. */
export interface TokenRatesDraft {
  input: string;
  output: string;
  cacheRead: string;
  cacheWrite: string;
}

/** One set of per-million-token rates as numbers, keyed like {@link TokenRatesDraft}. */
export type TokenRates = Record<keyof TokenRatesDraft, number>;

/** A model's AWS cost in USD per MTok, or `null` when it has none. */
export function costRates(model: BedrockCatalogModel): TokenRates | null {
  const p = model.cost;
  return p === null
    ? null
    : {
        input: p.input_usd_per_mtok,
        output: p.output_usd_per_mtok,
        cacheRead: p.cache_read_usd_per_mtok,
        cacheWrite: p.cache_write_usd_per_mtok,
      };
}

/** A model's customer price in RAU per MTok, or `null` when it has none. */
export function priceRates(model: BedrockCatalogModel): TokenRates | null {
  const p = model.customer_price;
  return p === null
    ? null
    : {
        input: p.input_rau_per_mtok,
        output: p.output_rau_per_mtok,
        cacheRead: p.cache_read_rau_per_mtok,
        cacheWrite: p.cache_write_rau_per_mtok,
      };
}

/** The catalog model form's fields, as typed text until saved. */
export interface BedrockModelDraft {
  label: string;
  description: string;
  model_id: string;
  /** What AWS charges Roboto, in USD per million tokens. */
  cost: TokenRatesDraft;
  /** What the customer is charged, in RAU per million tokens. */
  price: TokenRatesDraft;
  context_window_tokens: string;
  /** The model's own output limit; blank sets none. */
  max_output_tokens: string;
  /** Capability flags set by the admin; the rest follow the family. */
  capability_overrides: Partial<ModelCapabilities>;
  /** Vision settings set by the admin; the rest follow the family. */
  vision: VisionDraft;
}

/** Rates as form text; all blank when there are none. */
function ratesText(rates: TokenRates | null): TokenRatesDraft {
  const text = (key: keyof TokenRatesDraft) =>
    rates === null ? "" : String(rates[key]);
  return {
    input: text("input"),
    output: text("output"),
    cacheRead: text("cacheRead"),
    cacheWrite: text("cacheWrite"),
  };
}

/** A token count as form text; blank for none. */
export function tokensText(value: number | null | undefined): string {
  return value === null || value === undefined ? "" : String(value);
}

/** Form text as a token count; `null` when blank. */
function tokensValue(text: string): number | null {
  const trimmed = text.trim();
  return trimmed === "" ? null : Number(trimmed);
}

/** A form for a new model, or pre-filled from an existing one. */
export function draftFromModel(
  model: BedrockCatalogModel | undefined,
): BedrockModelDraft {
  return {
    label: model?.label ?? "",
    description: model?.description ?? "",
    model_id: model?.model_id ?? "",
    cost: ratesText(model === undefined ? null : costRates(model)),
    price: ratesText(model === undefined ? null : priceRates(model)),
    context_window_tokens: tokensText(model?.context_window_tokens),
    max_output_tokens: tokensText(model?.max_output_tokens),
    capability_overrides: model?.capability_overrides ?? {},
    vision: visionDraftFromOverrides(model?.vision_overrides),
  };
}

/** A non-negative decimal, or `null` when the text is not one. Blank reads as `blank`. */
function parseRate(text: string, blank: number | null): number | null {
  const trimmed = text.trim();
  if (trimmed === "") {
    return blank;
  }
  if (!/^\d+(\.\d+)?$/.test(trimmed)) {
    return null;
  }
  return Number(trimmed);
}

/**
 * Whether the draft's model may use prompt caching: its own override when
 * set, else the saved model's effective capability. A new model's family is
 * not known here, so it may.
 */
export function mayUsePromptCaching(
  draft: BedrockModelDraft,
  effective: ModelCapabilities | undefined,
): boolean {
  return (
    draft.capability_overrides.prompt_caching ??
    effective?.prompt_caching ??
    true
  );
}

/**
 * Why one set of rates is incomplete, or `null` when it is not: input and
 * output are required, and cache rates too while prompt caching may be on
 * (0 is allowed). `name` and `unit` word the message.
 */
function ratesError(
  rates: TokenRatesDraft,
  promptCaching: boolean,
  { name, unit }: { name: string; unit: string },
): string | null {
  if (
    parseRate(rates.input, null) === null ||
    parseRate(rates.output, null) === null
  ) {
    return `${name} input and output are required, in ${unit} per million tokens.`;
  }
  // A blank cache rate would bill (or record) every cached token at 0.
  const blankCache = promptCaching ? null : 0;
  if (
    parseRate(rates.cacheRead, blankCache) === null ||
    parseRate(rates.cacheWrite, blankCache) === null
  ) {
    return `${name} cache read and write are required while prompt caching is on, in ${unit} per million tokens (0 is allowed).`;
  }
  return null;
}

/**
 * Why the draft cannot be saved yet, or `null` when it can. A model needs
 * both its AWS cost and its customer price, each with input and output, and
 * cache rates too unless prompt caching is off (`effective` is the saved
 * model's capabilities). Its vision settings must pass
 * {@link visionDraftError} against the family's (`visionDefaults`, unknown
 * for a new model).
 */
export function bedrockModelDraftError(
  draft: BedrockModelDraft,
  effective?: ModelCapabilities,
  visionDefaults?: VisionSettings,
): string | null {
  if (draft.label.trim() === "") {
    return "A label is required.";
  }
  if (draft.model_id.trim() === "") {
    return "A Bedrock model id is required.";
  }
  const promptCaching = mayUsePromptCaching(draft, effective);
  const error =
    ratesError(draft.cost, promptCaching, {
      name: "AWS cost",
      unit: "USD",
    }) ??
    ratesError(draft.price, promptCaching, {
      name: "Customer price",
      unit: "RAU",
    });
  if (error !== null) {
    return error;
  }
  const window = draft.context_window_tokens.trim();
  if (window !== "" && !/^[1-9]\d*$/.test(window)) {
    return "The context window must be a whole number of tokens.";
  }
  const outputLimit = draft.max_output_tokens.trim();
  if (outputLimit !== "" && !/^[1-9]\d*$/.test(outputLimit)) {
    return "The output limit must be a positive whole number of tokens.";
  }
  return visionDraftError(draft.vision, visionDefaults);
}

/**
 * The create or update body for a draft that passed
 * {@link bedrockModelDraftError}. Default membership is not edited here:
 * a create starts outside the Roboto default (the server refuses one in it),
 * and an update keeps the model's current membership.
 */
export function bedrockModelRequest(
  draft: BedrockModelDraft,
  existing: BedrockCatalogModel | undefined,
): BedrockModelRequest {
  const cost: BedrockModelCost = {
    input_usd_per_mtok: parseRate(draft.cost.input, null) ?? 0,
    output_usd_per_mtok: parseRate(draft.cost.output, null) ?? 0,
    cache_read_usd_per_mtok: parseRate(draft.cost.cacheRead, 0) ?? 0,
    cache_write_usd_per_mtok: parseRate(draft.cost.cacheWrite, 0) ?? 0,
  };
  const customer_price: BedrockModelCustomerPrice = {
    input_rau_per_mtok: parseRate(draft.price.input, null) ?? 0,
    output_rau_per_mtok: parseRate(draft.price.output, null) ?? 0,
    cache_read_rau_per_mtok: parseRate(draft.price.cacheRead, 0) ?? 0,
    cache_write_rau_per_mtok: parseRate(draft.price.cacheWrite, 0) ?? 0,
  };
  const vision_overrides = visionOverridesFromDraft(draft.vision);
  return {
    label: draft.label.trim(),
    description: draft.description.trim(),
    model_id: draft.model_id.trim(),
    cost,
    customer_price,
    context_window_tokens: tokensValue(draft.context_window_tokens),
    max_output_tokens: tokensValue(draft.max_output_tokens),
    in_roboto_default: existing?.in_roboto_default ?? false,
    is_default: existing?.is_default ?? false,
    // Only set flags are sent. An update always sends them, since `{}` is
    // how every override is cleared; a create sends them only when some are
    // set.
    ...((existing !== undefined ||
      Object.keys(draft.capability_overrides).length > 0) && {
      capability_overrides: draft.capability_overrides,
    }),
    // Vision overrides follow the same rule.
    ...((existing !== undefined ||
      Object.keys(vision_overrides).length > 0) && { vision_overrides }),
  };
}

/**
 * The update body that re-sends `model` as stored, with `patch` applied.
 * Capability and vision overrides are omitted, which keeps them.
 */
export function catalogModelRequest(
  model: BedrockCatalogModel,
  patch: Pick<BedrockModelRequest, "in_roboto_default" | "is_default">,
): BedrockModelRequest {
  return {
    label: model.label,
    description: model.description,
    model_id: model.model_id,
    cost: model.cost,
    customer_price: model.customer_price,
    context_window_tokens: model.context_window_tokens,
    max_output_tokens: model.max_output_tokens,
    in_roboto_default: model.in_roboto_default,
    is_default: model.is_default,
    ...patch,
  };
}

/** The four token rates, in the order every rate table lists them. */
export const TOKEN_RATE_KEYS: readonly (keyof TokenRatesDraft)[] = [
  "input",
  "output",
  "cacheRead",
  "cacheWrite",
];

/** Display name of each token rate. */
export const TOKEN_RATE_LABELS: Record<keyof TokenRatesDraft, string> = {
  input: "Input",
  output: "Output",
  cacheRead: "Cache read",
  cacheWrite: "Cache write",
};

/** An AWS cost, "$0.3". */
export function usdText(usd: number): string {
  return `$${usd}`;
}

/** Input and output customer price, "30 / 150 RAU". */
export function priceSummary(price: TokenRates): string {
  return `${price.input} / ${price.output} RAU`;
}

/** Input and output AWS cost, "cost $3 / $15". */
export function costSummary(cost: TokenRates): string {
  return `cost ${usdText(cost.input)} / ${usdText(cost.output)}`;
}

/**
 * How far a price is above the break-even price of its cost:
 * price / (cost × {@link BREAK_EVEN_RAU_PER_USD}) − 1, so 0.25 is 25% over.
 * `null` when the cost is 0 (any price is pure margin) or either is unset.
 */
export function rateMargin(
  usd: number | null,
  rau: number | null,
): number | null {
  if (usd === null || rau === null || usd === 0) {
    return null;
  }
  return rau / (usd * BREAK_EVEN_RAU_PER_USD) - 1;
}

/** A margin as "+25%", "−10%" or "break-even", to a tenth of a percent. */
export function marginText(margin: number): string {
  const percent = Math.round(margin * 1000) / 10;
  if (percent === 0) {
    return "break-even";
  }
  return percent > 0 ? `+${percent}%` : `−${-percent}%`;
}

/**
 * One rate's margin in the form, as {@link marginText}, or `null` while its
 * cost is 0 or either value is blank or not a number.
 */
export function draftRateMargin(
  draft: Pick<BedrockModelDraft, "cost" | "price">,
  key: keyof TokenRatesDraft,
): string | null {
  const margin = rateMargin(
    parseRate(draft.cost[key], null),
    parseRate(draft.price[key], null),
  );
  return margin === null ? null : marginText(margin);
}

/**
 * The one margin a model's catalog price cell shows: the lower of its input
 * and output margins, so it never overstates what Roboto keeps on either.
 * `null` when both input and output cost 0.
 */
export function headlineMargin(
  cost: TokenRates,
  price: TokenRates,
): number | null {
  const margins = (["input", "output"] as const)
    .map((key) => rateMargin(cost[key], price[key]))
    .filter((margin): margin is number => margin !== null);
  return margins.length === 0 ? null : Math.min(...margins);
}

/**
 * Every rate of a priced model in one sentence, for the accessible name of
 * its catalog cell (a tooltip reaches no screen reader): "Input $3 cost,
 * 30 RAU price, break-even; Output …".
 */
export function describeRates(cost: TokenRates, price: TokenRates): string {
  return TOKEN_RATE_KEYS.map((key) => {
    const margin = rateMargin(cost[key], price[key]);
    const marginPart = margin === null ? "" : `, ${marginText(margin)}`;
    return `${TOKEN_RATE_LABELS[key]} ${usdText(cost[key])} cost, ${price[key]} RAU price${marginPart}`;
  }).join("; ");
}

/** `usd` × {@link BREAK_EVEN_RAU_PER_USD} as form text, without float noise. */
function breakEvenText(usd: number): string {
  return String(Math.round(usd * BREAK_EVEN_RAU_PER_USD * 1e6) / 1e6);
}

/**
 * The customer price that breaks even on the draft's AWS cost: each cost
 * × {@link BREAK_EVEN_RAU_PER_USD}. A rate whose cost is blank or not a
 * number keeps its current price. `null` until input and output costs are
 * both set, since there is nothing to fill from.
 */
export function breakEvenPrice(
  draft: Pick<BedrockModelDraft, "cost" | "price">,
): TokenRatesDraft | null {
  const { cost, price } = draft;
  if (
    parseRate(cost.input, null) === null ||
    parseRate(cost.output, null) === null
  ) {
    return null;
  }
  const fill = (key: keyof TokenRatesDraft) => {
    const usd = parseRate(cost[key], null);
    return usd === null ? price[key] : breakEvenText(usd);
  };
  return {
    input: fill("input"),
    output: fill("output"),
    cacheRead: fill("cacheRead"),
    cacheWrite: fill("cacheWrite"),
  };
}

/**
 * Why a model cannot be deleted, or `undefined` when it can: it is the
 * default, it is bound to a role, or an org uses it.
 */
export function deleteBlocker(model: BedrockCatalogModel): string | undefined {
  if (model.is_default) {
    return "The default model cannot be deleted.";
  }
  const roles: readonly BedrockModelRole[] = model.bound_roles ?? [];
  if (roles.length > 0) {
    const names =
      roles.length === 1
        ? `the ${roles[0]} role`
        : `the ${roles.slice(0, -1).join(", ")} and ${roles[roles.length - 1]} roles`;
    const pronoun = roles.length === 1 ? "it" : "them";
    return `Bound to ${names}; bind ${pronoun} to another model first.`;
  }
  if (model.used_by_org_count > 0) {
    const orgs = model.used_by_org_count === 1 ? "org" : "orgs";
    return `Used by ${model.used_by_org_count} ${orgs}; remove it from them first.`;
  }
  return undefined;
}

export { offerBlocker } from "@/shared/domain/orgs/orgModelConfig";
